import React, { useState, useEffect } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  Button,
  Label,
  Input,
  FormFeedback,
  Form,
  Badge,
  Spinner
} from "reactstrap";
import * as Yup from "yup";
import { useFormik } from "formik";
import { useSelector, useDispatch } from "react-redux";
import { editProfile, resetProfileFlag } from "../../slices/thunks";
import { createSelector } from "reselect";
import { getLoggedInUser } from "../../api/api";
import { toast } from "react-toastify";

const UserProfile = () => {
  document.title = "Perfil de Usuario | Billing Engine";
  const dispatch = useDispatch<any>();

  const [user, setUser] = useState<any>(null);
  const [isEditing, setIsEditing] = useState(false);

  const selectLayoutState = (state: any) => state.Profile;
  const userprofileData = createSelector(
    selectLayoutState,
    (state) => ({
      success: state.success,
      error: state.error,
      loading: state.loading
    })
  );

  const { success, error, loading } = useSelector(userprofileData);

  useEffect(() => {
    const loggedInUser = getLoggedInUser();
    if (loggedInUser) {
        if (loggedInUser.data && Array.isArray(loggedInUser.data) && loggedInUser.data.length > 0) {
             setUser(loggedInUser.data[0]);
        } else if (loggedInUser.data && !Array.isArray(loggedInUser.data)) {
             setUser(loggedInUser.data);
        } else {
             setUser(loggedInUser);
        }
    } else {
    }
  }, []);

  useEffect(() => {
    if (success) {
        toast.success("Perfil actualizado con éxito");
        setIsEditing(false);
        setTimeout(() => dispatch(resetProfileFlag()), 3000);
    }
    if (error) {
        toast.error(error);
        setTimeout(() => dispatch(resetProfileFlag()), 3000);
    }
  }, [dispatch, success, error]);

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      name: user?.name || "",
      email: user?.email || "",
      document: user?.document || "",
      phone: user?.jsonData?.phone || "",
    },
    validationSchema: Yup.object({
      name: Yup.string().required("El nombre es requerido"),
      email: Yup.string().email("Email inválido").required("El email es requerido"),
      document: Yup.string().required("El documento es requerido"),
      phone: Yup.string(),
    }),
    onSubmit: (values) => {
      const updatedUser = {
        ...user,
        name: values.name,
        email: values.email,
        document: values.document,
        jsonData: {
            ...user?.jsonData,
            phone: values.phone
        }
      };
      
      dispatch(editProfile({
          ...updatedUser,
          username: values.name, 
          idx: user.id
      }));
      
      setUser(updatedUser);
      const loggedInUser = getLoggedInUser();
      let newStorageData = loggedInUser;
      
      if (loggedInUser.data && Array.isArray(loggedInUser.data) && loggedInUser.data.length > 0) {
          newStorageData.data[0] = updatedUser;
      } else if (loggedInUser.data && !Array.isArray(loggedInUser.data)) {
          newStorageData.data = updatedUser;
      } else {
          newStorageData = updatedUser;
      }
      
      localStorage.setItem("user", JSON.stringify(newStorageData));
    }
  });

  if (!user) {
      return (
        <div className="page-content d-flex justify-content-center align-items-center" style={{ minHeight: '60vh' }}>
            <Spinner color="primary" />
        </div>
      );
  }

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Row>
            <Col lg="12">
              <Card>
                <div className="bg-primary profile-wid-bg position-relative" style={{ height: '120px' }}>
                    <div className="position-absolute top-0 start-0 w-100 h-100 bg-soft-dark opacity-25"></div>
                </div>
                <CardBody className="pt-0">
                  <div className="text-center">
                    <div className="profile-user position-relative d-inline-block mx-auto mb-4">
                      <img
                        src=""
                        alt=""
                        className="rounded-circle avatar-xl img-thumbnail user-profile-image shadow"
                        style={{ marginTop: '-60px' }}
                      />
                    </div>
                    <h5 className="fs-17 mb-1 text-dark fw-bold">{user.name}</h5>
                    <p className="text-muted mb-2">{user.email}</p>
                    <Badge color={user.available ? "success" : "danger"} className="mb-3 px-3 py-1 fs-11">
                        {user.available ? "Disponible" : "No Disponible"}
                    </Badge>
                  </div>

                  <div className="d-flex justify-content-center mb-4">
                      {!isEditing && (
                          <Button color="primary" className="btn-label waves-effect waves-light" onClick={() => setIsEditing(true)}>
                              <i className="ri-pencil-line label-icon align-middle fs-16 me-2"></i> Editar Perfil
                          </Button>
                      )}
                  </div>

                  <Form
                    onSubmit={(e) => {
                      e.preventDefault();
                      validation.handleSubmit();
                      return false;
                    }}
                  >
                    <div className="row justify-content-center">
                        <Col lg={8}>
                            {isEditing ? (
                                <div className="row g-3">
                                    <Col md={6}>
                                        <Label className="form-label">Nombre Completo</Label>
                                        <Input
                                            name="name"
                                            type="text"
                                            onChange={validation.handleChange}
                                            onBlur={validation.handleBlur}
                                            value={validation.values.name}
                                            invalid={!!validation.touched.name && !!validation.errors.name}
                                        />
                                        {validation.touched.name && validation.errors.name ? (
                                            <FormFeedback>{validation.errors.name as string}</FormFeedback>
                                        ) : null}
                                    </Col>
                                    <Col md={6}>
                                        <Label className="form-label">Correo Electrónico</Label>
                                        <Input
                                            name="email"
                                            type="email"
                                            onChange={validation.handleChange}
                                            onBlur={validation.handleBlur}
                                            value={validation.values.email}
                                            invalid={!!validation.touched.email && !!validation.errors.email}
                                            disabled 
                                        />
                                    </Col>
                                    <Col md={6}>
                                        <Label className="form-label">Documento</Label>
                                        <Input
                                            name="document"
                                            type="text"
                                            onChange={validation.handleChange}
                                            onBlur={validation.handleBlur}
                                            value={validation.values.document}
                                            invalid={!!validation.touched.document && !!validation.errors.document}
                                        />
                                        {validation.touched.document && validation.errors.document ? (
                                            <FormFeedback>{validation.errors.document as string}</FormFeedback>
                                        ) : null}
                                    </Col>
                                    <Col md={6}>
                                        <Label className="form-label">Teléfono</Label>
                                        <Input
                                            name="phone"
                                            type="text"
                                            onChange={validation.handleChange}
                                            onBlur={validation.handleBlur}
                                            value={validation.values.phone}
                                            invalid={!!validation.touched.phone && !!validation.errors.phone}
                                        />
                                        {validation.touched.phone && validation.errors.phone ? (
                                            <FormFeedback>{validation.errors.phone as string}</FormFeedback>
                                        ) : null}
                                    </Col>
                                    
                                    <div className="col-12 text-center mt-4">
                                        <div className="d-flex gap-2 justify-content-center">
                                            <Button type="button" color="light" onClick={() => setIsEditing(false)}>
                                                Cancelar
                                            </Button>
                                            <Button type="submit" color="success" disabled={loading}>
                                                {loading ? <Spinner size="sm" className="me-2" /> : null}
                                                Guardar Cambios
                                            </Button>
                                        </div>
                                    </div>
                                </div>
                            ) : (
                                <div className="table-responsive">
                                    <table className="table table-borderless mb-0">
                                        <tbody>
                                            <tr>
                                                <th className="ps-0" scope="row" style={{ width: '200px' }}>Nombre :</th>
                                                <td className="text-muted">{user.name}</td>
                                            </tr>
                                            <tr>
                                                <th className="ps-0" scope="row">Email :</th>
                                                <td className="text-muted">{user.email}</td>
                                            </tr>
                                            <tr>
                                                <th className="ps-0" scope="row">Documento :</th>
                                                <td className="text-muted">{user.document}</td>
                                            </tr>
                                            <tr>
                                                <th className="ps-0" scope="row">Teléfono :</th>
                                                <td className="text-muted">{user.jsonData?.phone || "N/A"}</td>
                                            </tr>
                                            <tr>
                                                <th className="ps-0" scope="row">Último Acceso :</th>
                                                <td className="text-muted">{user.lastLogin || "N/A"}</td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </Col>
                    </div>
                  </Form>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default UserProfile;
